import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Users as UsersIcon, X } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { fmtRelative } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface AdminUserRow {
  id: string;
  phone: string;
  name: string | null;
  role: 'business' | 'courier' | 'both' | 'admin';
  vehicle_mode: string | null;
  profile_image_url: string | null;
  telegram_linked: boolean;
  web_push_enabled: boolean;
  is_available: boolean;
  is_suspended: boolean;
  reliability_score: number;
  completed_count: number;
  cancellation_count: number;
  no_show_count: number;
  average_rating: number | null;
  last_location_updated_at: string | null;
  created_at: string;
}

const ROLES = ['business', 'courier', 'both', 'admin'] as const;
const VEHICLES = ['walker', 'bicycle', 'motorcycle', 'car', 'van', 'lorry'] as const;

function initials(s: string | null | undefined): string {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/).filter(Boolean);
  return parts.length === 1
    ? parts[0]!.slice(0, 2).toUpperCase()
    : (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const role = params.get('role') ?? '';
  const vehicle = params.get('vehicle') ?? '';
  const suspended = params.get('suspended') ?? '';
  const telegram = params.get('telegram') ?? '';
  const q = params.get('q') ?? '';

  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draftQ, setDraftQ] = useState(q);

  function patch(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged);
  }

  function load() {
    setRows(null);
    setErr(null);
    const qs = new URLSearchParams();
    if (role) qs.set('role', role);
    if (vehicle) qs.set('vehicle', vehicle);
    if (suspended) qs.set('suspended', suspended);
    if (telegram) qs.set('telegram', telegram);
    if (q) qs.set('q', q);
    api<{ users: AdminUserRow[] }>(`/api/admin/users${qs.toString() ? `?${qs}` : ''}`)
      .then((r) => setRows(r.users))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [role, vehicle, suspended, telegram, q]);

  const hasFilters = Boolean(role || vehicle || suspended || telegram || q);

  return (
    <Shell title="Users">
      <PageHeader
        title="All users"
        description="Search and manage businesses, couriers, and admins."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-2">
          <form
            className="relative flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              patch({ q: draftQ });
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Search by phone or name…"
              className="pl-9"
            />
          </form>
          <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
            <Select value={role || 'all'} onValueChange={(v) => patch({ role: v === 'all' ? '' : v })}>
              <SelectTrigger className="md:w-32"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any role</SelectItem>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={vehicle || 'all'} onValueChange={(v) => patch({ vehicle: v === 'all' ? '' : v })}>
              <SelectTrigger className="md:w-36"><SelectValue placeholder="Vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any vehicle</SelectItem>
                {VEHICLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={suspended || 'any'} onValueChange={(v) => patch({ suspended: v === 'any' ? '' : v })}>
              <SelectTrigger className="md:w-32"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any status</SelectItem>
                <SelectItem value="false">Active</SelectItem>
                <SelectItem value="true">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Select value={telegram || 'any'} onValueChange={(v) => patch({ telegram: v === 'any' ? '' : v })}>
              <SelectTrigger className="md:w-36"><SelectValue placeholder="Telegram" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any TG</SelectItem>
                <SelectItem value="true">TG linked</SelectItem>
                <SelectItem value="false">No TG</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="md:h-9"
                onClick={() => {
                  setDraftQ('');
                  setParams(new URLSearchParams());
                }}
              >
                <X className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Channels</TableHead>
              <TableHead className="text-right tnum">Done</TableHead>
              <TableHead className="text-right tnum">Cancel</TableHead>
              <TableHead className="text-right tnum">Score</TableHead>
              <TableHead className="text-right tnum">Rating</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="text-right">Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={10}><Skeleton className="h-4 w-full" /></TableCell>
              </TableRow>
            ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <EmptyState
                    icon={UsersIcon}
                    title={hasFilters ? 'No users match these filters.' : 'No users yet.'}
                  />
                </TableCell>
              </TableRow>
            )}
            {rows?.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <Link to={`/users/${u.id}`} className="flex items-center gap-3 hover:text-brand-700 dark:hover:text-brand-500">
                    <Avatar className="h-9 w-9">
                      {u.profile_image_url && <AvatarImage src={u.profile_image_url} alt="" />}
                      <AvatarFallback>{initials(u.name ?? u.phone)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text">{u.name ?? '—'}</p>
                      <p className="tnum truncate text-xs text-text-muted">{u.phone}</p>
                    </div>
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge variant={u.role === 'admin' ? 'brand' : 'default'}>{u.role}</Badge>
                </TableCell>
                <TableCell className="text-xs text-text-subtle">{u.vehicle_mode ?? '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {u.telegram_linked && <Badge>TG</Badge>}
                    {u.web_push_enabled && <Badge>push</Badge>}
                    {!u.telegram_linked && !u.web_push_enabled && (
                      <span className="text-xs text-text-muted">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right tnum">{u.completed_count}</TableCell>
                <TableCell className="text-right tnum">{u.cancellation_count}</TableCell>
                <TableCell className="text-right tnum">{u.reliability_score.toFixed(1)}</TableCell>
                <TableCell className="text-right tnum">
                  {u.average_rating != null ? u.average_rating.toFixed(1) : '—'}
                </TableCell>
                <TableCell>
                  {u.is_suspended ? (
                    <Badge variant="urgent">suspended</Badge>
                  ) : (
                    <Badge variant="completed">active</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs text-text-muted">
                  {fmtRelative(u.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Shell>
  );
}
